/**
 * Fractional indexing utility for page sort order.
 *
 * Computes a lexicographically sortable string between two adjacent sort keys.
 * Used for drag-and-drop reordering without rewriting all sibling sort orders.
 *
 * Strategy: find the midpoint character-by-character in base-36 space.
 * If the two keys are adjacent (no room), append a midpoint character.
 */

const BASE = 36;
const MID_CHAR = 'i'; // midpoint of 0-z range

/**
 * Compute a sort key between `before` and `after`.
 * - If both are provided, returns a key lexicographically between them.
 * - If only `before` is provided, returns a key after it (append suffix).
 * - If only `after` is provided, returns a key before it (prepend prefix).
 * - If neither is provided, returns a default midpoint.
 */
export function midpoint(before: string | null, after: string | null): string {
  if (!before && !after) return MID_CHAR;

  if (!before) {
    // Insert before `after` — prepend a character earlier in the alphabet
    const firstChar = after!.charCodeAt(0);
    if (firstChar > '1'.charCodeAt(0)) {
      return String.fromCharCode(Math.floor((firstChar + '0'.charCodeAt(0)) / 2));
    }
    return '0' + MID_CHAR;
  }

  if (!after) {
    // Insert after `before` — append a suffix
    return before + MID_CHAR;
  }

  // Insert between `before` and `after`
  // Find the first position where they differ, then pick the midpoint
  const maxLen = Math.max(before.length, after.length) + 1;

  for (let i = 0; i < maxLen; i++) {
    const bChar = i < before.length ? before.charCodeAt(i) : '0'.charCodeAt(0);
    const aChar = i < after.length ? after.charCodeAt(i) : 'z'.charCodeAt(0) + 1;

    if (bChar < aChar - 1) {
      // Room between — pick the midpoint at this position
      const midChar = String.fromCharCode(Math.floor((bChar + aChar) / 2));
      return before.slice(0, i) + midChar;
    }

    if (bChar === aChar - 1 || bChar === aChar) {
      // Adjacent or equal at this position — continue to next position
      continue;
    }
  }

  // Fallback: append midpoint to `before`
  return before + MID_CHAR;
}
